import KpiChart from "@app/components/kpi-chart";
import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import { environment } from "@app/relay/relay-environment";
import type { cashPositionPage_Query } from "@app/queries/cashPositionPage_Query.graphql";
import { useEffect, useMemo, useState } from "react";
import { fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";
import CashPositionTxnsPanel, {
  type CashFlowCategory,
  type CashFlowDrill,
} from "./cash-position-txns-panel";

export const cashPositionQuery = graphql`
  query cashPositionPage_Query($from: String) {
    cashPosition(from: $from) {
      cashPosition {
        month
        value
      }
      netCashPosition {
        month
        value
      }
      debt {
        month
        value
      }
      revenue {
        month
        value
      }
      costs {
        month
        value
      }
      dividends {
        month
        value
      }
      loanProceeds {
        month
        value
      }
      loanRepayments {
        month
        value
      }
      tax {
        month
        value
      }
    }
    bankAccounts {
      id
      name
      nickname
      last4
      number
    }
    allStakeholders {
      id
      name
    }
  }
`;

type Toggle = "revenue" | "costs" | "tax" | "dividends" | "loanProceeds" | "loanRepayments" | "debt";

// Bars in the same `stack` stack on top of each other; bars in different stacks
// render as separate columns. Tax payments, dividends and loan repayments stack
// onto the costs column; loan proceeds stacks onto the revenue column (the base
// series is listed first, so it sits at the bottom — tax sits directly above the
// costs base as a distinct, non-operating outflow to the government, not a normal
// cost). `drill` flags the bars whose click opens the txns drawer.
const TOGGLES: {
  key: Toggle;
  label: string;
  color: string;
  type: "bar" | "line";
  stack?: string;
  drill?: CashFlowCategory;
}[] = [
  { key: "revenue", label: "Revenue", color: "#16a34a", type: "bar", stack: "revenue", drill: "revenue" },
  { key: "costs", label: "Costs", color: "#dc2626", type: "bar", stack: "costs", drill: "cost" },
  { key: "tax", label: "Tax payments", color: "#db2777", type: "bar", stack: "costs", drill: "tax" },
  { key: "dividends", label: "Dividends", color: "#d97706", type: "bar", stack: "costs", drill: "dividends" },
  { key: "loanRepayments", label: "Loan repayments", color: "#4f46e5", type: "bar", stack: "costs", drill: "loanRepayments" },
  { key: "loanProceeds", label: "Loan proceeds", color: "#2563eb", type: "bar", stack: "revenue", drill: "loanProceeds" },
  { key: "debt", label: "Outstanding debt", color: "#7c3aed", type: "line" },
];

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function CashPositionPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<cashPositionPage_Query>;
}) {
  const preloaded = usePreloadedQuery(cashPositionQuery, queryRef);
  // Mirror the series locally so an in-place fix (drill-down mutation) can refresh
  // the chart without a route navigation. Re-sync only when the route hands a NEW
  // query (keyed on queryRef, not preloaded) — keying on preloaded would re-emit
  // the stale snapshot and clobber a refresh (the relay local-mirror clobber).
  const [cp, setCp] = useState(preloaded.cashPosition);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setCp(preloaded.cashPosition), [queryRef]);

  const [visible, setVisible] = useState<Record<Toggle, boolean>>({
    revenue: true,
    costs: true,
    tax: true,
    dividends: true,
    loanProceeds: true,
    loanRepayments: true,
    debt: false,
  });
  const [drill, setDrill] = useState<CashFlowDrill | null>(null);

  const accountOptions: AccountOption[] = preloaded.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const stakeholderOptions: StakeholderOption[] = preloaded.allStakeholders.filter(isRealStakeholder);

  const refresh = () =>
    fetchQuery<cashPositionPage_Query>(environment, cashPositionQuery, {
      from: dateRangeFromISO(getDateRange()) ?? undefined,
    })
      .toPromise()
      .then((fresh) => {
        if (fresh) setCp(fresh.cashPosition);
      });

  // A bar click reports the series label; map it back to the drill category.
  const openDrill = (seriesName: string, month: string) => {
    const toggle = TOGGLES.find((t) => t.label === seriesName);
    if (!toggle?.drill) return;
    setDrill({ category: toggle.drill, month, label: `${seriesName} · ${month}` });
  };

  // The two cumulative lines are always shown: Available cash (the bank balance)
  // and Net cash position (available minus outstanding debt). They define the
  // shared X-axis. Each visible bar carries its `stack` so KpiChart stacks the
  // ones that share a key; line toggles (debt) carry no stack.
  const series = useMemo(
    () => [
      {
        name: "Available cash",
        points: cp.cashPosition,
        type: "line" as const,
        color: "#111827",
      },
      {
        name: "Net cash position",
        points: cp.netCashPosition,
        type: "line" as const,
        color: "#0d9488",
      },
      ...TOGGLES.filter((t) => visible[t.key]).map((t) => ({
        name: t.label,
        points: cp[t.key],
        type: t.type,
        color: t.color,
        ...(t.type === "bar" ? { stack: t.stack } : {}),
      })),
    ],
    [cp, visible]
  );

  if (cp.cashPosition.length === 0) {
    return (
      <div className="p-6 text-gray-500 space-y-2">
        <h1 className="text-xl font-bold text-gray-900">Cash position</h1>
        <p>
          No transactions imported yet. Upload bank statements to see your cash
          position.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <h1 className="text-xl font-bold">Cash position</h1>
      <p className="text-xs text-gray-400">
        This screen always shows cash — the basis switch does not apply to actual
        bank balances. Click a flow bar to drill into its transactions.
      </p>

      <div className="flex items-center gap-4 bg-white rounded border p-4">
        <span className="text-xs font-medium text-gray-500">Show flows</span>
        {TOGGLES.map((t) => (
          <label key={t.key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={visible[t.key]}
              onChange={(e) =>
                setVisible((v) => ({ ...v, [t.key]: e.target.checked }))
              }
            />
            <span style={{ color: t.color }}>{t.label}</span>
          </label>
        ))}
      </div>

      <KpiChart
        // Remount on series-set change: KpiChart's echarts-for-react merges
        // options by default, so a deselected bar would otherwise linger. The
        // key forces a fresh chart instance whenever the visible series change.
        key={series.map((s) => s.name).join("|")}
        title="Available cash vs net cash position (lines) and monthly flows (bars)"
        series={series}
        format="money"
        onMonthClick={() => {}}
        onBarClick={openDrill}
      />

      {drill && (
        <CashPositionTxnsPanel
          drill={drill}
          accountOptions={accountOptions}
          stakeholderOptions={stakeholderOptions}
          onClose={() => setDrill(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
